import { useFormContext, useFormField } from '@calendly/form';
import { I18n } from '@calendly/i18n';
import classNames from 'classnames';
import { isEmpty, uniq } from 'lodash-es';
import moment from 'moment-timezone';
import { forwardRef, useMemo } from 'react';
import { useGetUserQuery } from 'common';
import { encodeAvailabilityRule } from 'components/availability/form/utils';
import { filterActiveRules } from 'components/availability/utils';
import { summarizeAvailabilityRule } from 'components/event_type_edit';
import { useEventType } from 'features/event_type_provider';
import { useETEditorFormikPoolingType } from 'services/event_types';
import { formatDate } from 'services/time_format';
import { AvailabilitySummaryMessage, findAvailabilityMembership } from '../../availability';
import { filterEnabledMemberships } from '../../helpers';
import { WarningMessage } from '../../warning_message';
const containerCss = "c1wa3ab";
const Container = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(containerCss, props.className)} />;
});
const hasNoAvailability = ({
  availableHours,
  timezone,
  dateOverrides
}) => {
  if (!availableHours || !dateOverrides) {
    return false;
  }
  return summarizeAvailabilityRule(encodeAvailabilityRule(availableHours, filterActiveRules(dateOverrides), timezone)) === '' && dateOverrides.length === 0;
};
const hasEmptyUserPools = ({
  memberships,
  userPools
}) => {
  if (!userPools) {
    return false;
  }
  const userPoolsUuids = userPools.map(({
    uuid,
    key
  }) => uuid || key);
  const membersUserPoolsUuids = memberships.filter(({
    user_pool_uid: upUuid
  }) => upUuid).map(member => member.user_pool_uid);
  return userPoolsUuids.length !== uniq(membersUserPoolsUuids).length;
};
const isUserMember = membership => !(membership.is_pending || membership.isPending);
const filterPoolMemberships = (pool, memberships) => memberships.filter(({
  user_pool_uid: upUuid
}) => upUuid === pool.uuid || pool.key);
const anyCrrAvailabilityErrors = ({
  userPools,
  memberships
}) => {
  const collectivePool = userPools.find(p => p.pooling_type === 'collective');
  const rrPools = userPools.filter(p => p.pooling_type === 'round_robin');
  if (collectivePool && filterPoolMemberships(collectivePool, memberships).find(m => hasNoAvailability(m))) {
    return true;
  }
  if (rrPools.length && rrPools.some(pool => filterPoolMemberships(pool, memberships).every(m => hasNoAvailability(m)))) {
    return true;
  }
  return false;
};
const getFormPath = ({
  isSingleHost,
  memberships,
  eventTypeMembershipsAttributes,
  isTeam,
  shareOverrideAvailability
}) => {
  if (shareOverrideAvailability) {
    return 'shareOverrideAvailability';
  }
  if (isSingleHost) {
    const membership = isTeam ? filterEnabledMemberships(memberships)[0] : memberships[0];
    const formMembership = findAvailabilityMembership(eventTypeMembershipsAttributes, membership);
    const formMembershipIndex = eventTypeMembershipsAttributes.indexOf(formMembership);
    return `event_type_memberships_attributes.${formMembershipIndex}`;
  }
  return 'eventTypeAvailability';
};
const AdhocEventTypeAvailabilitySummary = ({
  expiresAt,
  expired,
  groupedSpots,
  timezone
}) => {
  if (!expiresAt) return null;
  const timesText = I18n.t('publisher.event_type_editor.sections.availability.summary.spots', {
    count: (groupedSpots || []).length
  });
  const shortUntilDateText = formatDate(moment.tz(expiresAt, timezone), I18n.t('time.formats.date_large'));
  if (expired) {
    return <Container>
        <WarningMessage message={I18n.t('publisher.event_type_editor.sections.scheduling_settings.date_in_past_warning')} />
      </Container>;
  }
  return <Container>
      <span>
        {`${timesText} ${shortUntilDateText}`}
      </span>
    </Container>;
};
export const Summary = () => {
  const eventType = useEventType();
  const {
    data: currentUser
  } = useGetUserQuery();
  const {
    id,
    isGroup,
    isUser,
    isTeam,
    isMETParent
  } = eventType;
  const {
    isCollective,
    isRoundRobin,
    isCollectiveRoundRobin
  } = useETEditorFormikPoolingType();
  const {
    values: {
      userRuleType,
      memberships,
      event_type_memberships_attributes: eventTypeMembershipsAttributes,
      userPools,
      shareOverrideAvailability
    }
  } = useFormContext();
  const activeMemberships = filterEnabledMemberships(memberships).filter(isUserMember);
  const activeETMA = filterEnabledMemberships(eventTypeMembershipsAttributes).filter(isUserMember);
  const isExistingUserRuleType = userRuleType === 'existing';
  const isSingleHost = isUser || isTeam && isGroup;
  const formPath = useMemo(() => getFormPath({
    isSingleHost,
    memberships,
    eventTypeMembershipsAttributes,
    isTeam,
    shareOverrideAvailability
  }), [isSingleHost, memberships, eventTypeMembershipsAttributes, isTeam, shareOverrideAvailability]);
  const [{
    value = {}
  }] = useFormField({
    name: formPath
  });
  const {
    availableHours,
    timezone,
    dateOverrides
  } = value;
  if (eventType.isAdhocEventType) {
    return <AdhocEventTypeAvailabilitySummary timezone={timezone} groupedSpots={eventType.grouped_spots} expiresAt={eventType.expires_at} expired={eventType.expired} />;
  }
  if (eventType.period_type === 'fixed') {
    const to = moment(eventType.end_date);
    const today = moment().startOf('day');
    if (to < today) {
      return <Container>
          <WarningMessage message={I18n.t('publisher.event_type_editor.sections.scheduling_settings.date_in_past_warning')} />
        </Container>;
    }
  }
  if (id) {
    if (isMETParent) {
      if (isExistingUserRuleType) {
        return <Container>
            <span>
              {I18n.t('publisher.event_type_editor.sections.scheduling_settings.managed_event_types.primary_option')}
            </span>
          </Container>;
      }
      const {
        time_notation: timeNotation
      } = currentUser;
      const activeOverrides = filterActiveRules(dateOverrides);
      return <Container>
          <AvailabilitySummaryMessage availableHours={availableHours} timezone={timezone} dateOverrides={activeOverrides} timeNotation={timeNotation} size="medium" />
        </Container>;
    }
    if (isEmpty(activeMemberships) || isCollectiveRoundRobin && hasEmptyUserPools({
      userPools,
      memberships: activeMemberships
    })) {
      return <WarningMessage message={I18n.t('publisher.event_type_editor.sections.hosts_and_invitees.add_a_host_to_select')} />;
    }
    if (isExistingUserRuleType) {
      if (isCollective && activeETMA.find(m => hasNoAvailability(m))) {
        return <WarningMessage message={I18n.t('publisher.event_type_editor.no_availability_warning')} />;
      }
      if (isRoundRobin && activeETMA.every(m => hasNoAvailability(m))) {
        return <WarningMessage message={I18n.t('publisher.event_type_editor.no_availability_warning')} />;
      }
      if (isCollectiveRoundRobin && anyCrrAvailabilityErrors({
        userPools,
        memberships: activeMemberships.map(m => ({
          ...m,
          ...(activeETMA.find(({
            userId
          }) => userId === m.user_id) || {})
        }))
      })) {
        return <WarningMessage message={I18n.t('publisher.event_type_editor.no_availability_warning')} />;
      }
    }
  }
  if (isSingleHost || !isExistingUserRuleType) {
    const {
      time_notation: timeNotation
    } = currentUser;
    if (!availableHours || !dateOverrides) {
      return <WarningMessage message={I18n.t('publisher.event_type_editor.sections.hosts_and_invitees.add_a_host_to_select')} />;
    }
    const activeOverrides = filterActiveRules(dateOverrides);
    if (hasNoAvailability({
      availableHours,
      timezone,
      dateOverrides: activeOverrides
    })) {
      return <WarningMessage message={I18n.t('publisher.event_type_editor.no_availability_warning')} />;
    }
    return <Container>
        <AvailabilitySummaryMessage availableHours={availableHours} timezone={timezone} dateOverrides={activeOverrides} timeNotation={timeNotation} size="medium" />
      </Container>;
  }
  return <Container>
      <span>{I18n.t('publisher.event_type_editor.sections.availability.summary.multiple_hosts')}</span>
    </Container>;
};

require("./summary.wyw-in-js.css!=!../../../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./summary.js");